import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { useMcpRawQuery } from "../hooks/admin-queries"
import { RawJsonTab } from "./shared/raw-json-tab"

export type McpServerFormValue = {
  label: string
  url: string
  allowed_tools?: string[] | null
  require_approval?: string
  headers?: Record<string, string>
  enabled: boolean
  [key: string]: unknown
}

type EditMcpDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  agentName: string
  mcp: McpServerFormValue
  /** Called with partial updates when the Form tab is saved. */
  onSubmit: (updates: Partial<McpServerFormValue>) => Promise<void>
  /**
   * Called when the Raw tab is confirmed with the parsed entry, the audit
   * reason, and the optimistic-concurrency token from the raw GET. The
   * implementation MUST forward ``expectedHash`` to ``updateMcpServer`` —
   * the backend rejects raw saves without it.
   */
  onRawSave: (next: McpServerFormValue, reason: string, expectedHash: string) => Promise<void>
  isPending?: boolean
}

/**
 * Edit dialog for an existing MCP server.
 *
 * Complements ``add-mcp-dialog.tsx`` (which handles creation) by covering the
 * fields that previously could only be changed by deleting + re-adding:
 * url, allowed_tools, require_approval, and (via Raw mode) everything else.
 *
 * The label is intentionally read-only in the Form tab — the backend
 * enforces label immutability by taking it from the URL path. Raw mode can
 * replace the whole entry but still cannot rename via this endpoint (the
 * resource path wins).
 */
export function EditMcpDialog({
  isOpen,
  onOpenChange,
  agentName,
  mcp,
  onSubmit,
  onRawSave,
  isPending = false,
}: EditMcpDialogProps) {
  const { t } = useTranslation()
  // Fetch the raw entity + hash whenever the dialog is open. The Raw tab
  // shows the freshest stored doc (not the possibly-stale ``mcp`` prop)
  // and uses the returned hash for optimistic concurrency on save.
  const { data: rawData } = useMcpRawQuery(agentName, isOpen ? mcp.label : null, isOpen)
  const rawEntity = rawData?.entity ?? mcp
  const rawHash = rawData?.hash ?? ""
  const [url, setUrl] = useState(mcp.url)
  const [allowedTools, setAllowedTools] = useState((mcp.allowed_tools || []).join(", "))
  const [requireApproval, setRequireApproval] = useState(mcp.require_approval || "never")
  const [enabled, setEnabled] = useState(mcp.enabled)
  const [urlError, setUrlError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setUrl(mcp.url)
      setAllowedTools((mcp.allowed_tools || []).join(", "))
      setRequireApproval(mcp.require_approval || "never")
      setEnabled(mcp.enabled)
      setUrlError(null)
    }
  }, [isOpen, mcp])

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setUrlError(null)

    if (!url.trim()) {
      setUrlError(t("adminDashboard.mcpEdit.errorUrlRequired", { defaultValue: "URL is required" }))
      return
    }
    try {
      new URL(url.trim())
    } catch {
      setUrlError(
        t("adminDashboard.mcpEdit.errorUrlInvalid", { defaultValue: "Invalid URL format" })
      )
      return
    }

    const tools = allowedTools
      .split(",")
      .map((tool) => tool.trim())
      .filter(Boolean)

    await onSubmit({
      url: url.trim(),
      allowed_tools: tools.length > 0 ? tools : null,
      require_approval: requireApproval,
      enabled,
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.mcpEdit.title", { defaultValue: "Edit MCP server" })}{" "}
            <code className="font-mono text-sm">{mcp.label}</code>{" "}
            <span className="text-muted-foreground text-sm font-normal">({agentName})</span>
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.mcpEdit.description", {
              defaultValue:
                "Update the server's URL, tool allow-list, and approval policy. Use the Raw JSON tab for headers and other advanced fields.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.mcpEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.mcpEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4" value="form">
            <form className="space-y-4" onSubmit={handleFormSubmit}>
              <div className="space-y-2">
                <Label>{t("adminDashboard.mcpEdit.fieldLabel", { defaultValue: "Label" })}</Label>
                <Input disabled className="font-mono text-xs" value={mcp.label} />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.mcpEdit.fieldLabelHelp", {
                    defaultValue:
                      "Labels are authoritative from the URL path and cannot be renamed here. Delete and re-add to rename.",
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mcp-url">
                  {t("adminDashboard.mcpEdit.fieldUrl", { defaultValue: "URL" })}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="mcp-url"
                  type="url"
                  value={url}
                  placeholder={t("adminDashboard.mcpEdit.fieldUrlPlaceholder", {
                    defaultValue: "https://api.example.com/mcp",
                  })}
                  onChange={(event) => setUrl(event.target.value)}
                />
                {urlError ? <p className="text-destructive text-xs">{urlError}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mcp-allowed-tools">
                  {t("adminDashboard.mcpEdit.fieldAllowedTools", { defaultValue: "Allowed tools" })}
                </Label>
                <Input
                  id="mcp-allowed-tools"
                  value={allowedTools}
                  placeholder={t("adminDashboard.mcpEdit.fieldAllowedToolsPlaceholder", {
                    defaultValue: "tool1, tool2 (empty = allow all)",
                  })}
                  onChange={(event) => setAllowedTools(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.mcpEdit.fieldAllowedToolsHelp", {
                    defaultValue:
                      "Comma-separated. Leave empty to allow every tool exposed by the server.",
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mcp-approval">
                  {t("adminDashboard.mcpEdit.fieldRequireApproval", {
                    defaultValue: "Require approval",
                  })}
                </Label>
                <Select value={requireApproval} onValueChange={setRequireApproval}>
                  <SelectTrigger id="mcp-approval">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">
                      {t("adminDashboard.mcpEdit.approvalNever", { defaultValue: "Never" })}
                    </SelectItem>
                    <SelectItem value="always">
                      {t("adminDashboard.mcpEdit.approvalAlways", { defaultValue: "Always" })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="mcp-enabled">
                    {t("adminDashboard.mcpEdit.fieldEnabled", { defaultValue: "Enabled" })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.mcpEdit.fieldEnabledHelp", {
                      defaultValue:
                        "Disabled servers stay in the configuration but are skipped at runtime.",
                    })}
                  </p>
                </div>
                <Switch checked={enabled} id="mcp-enabled" onCheckedChange={setEnabled} />
              </div>

              <DialogFooter>
                <Button
                  disabled={isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.mcpEdit.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button disabled={isPending} type="submit">
                  {isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.mcpEdit.saving", { defaultValue: "Saving…" })}
                    </>
                  ) : (
                    t("adminDashboard.mcpEdit.save", { defaultValue: "Save" })
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={mcp.label}
              isPending={isPending || !rawHash}
              value={rawEntity}
              resourceLabel={t("adminDashboard.mcpEdit.rawResourceLabel", {
                defaultValue: "MCP server {{label}}",
                label: mcp.label,
              })}
              onSave={async (next, reason) => {
                await onRawSave(next as McpServerFormValue, reason, rawHash)
              }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
